/**
 * Briefing-freshness helper.
 *
 * Pure utility that turns a briefing's "last updated" timestamp into a muted
 * relative label ("updated 2 hours ago") plus a staleness flag, so the UI can
 * tell the reader at a glance whether the briefing is still on-cadence.
 *
 * The briefing cadence is roughly every 4 hours (weekday 8am / 12pm / 4pm ET),
 * so anything older than {@link STALENESS_WINDOW_MS} is considered stale.
 */

import { formatDistanceStrict } from 'date-fns';

/** Staleness window in milliseconds (~4h cadence, with a little slack). */
const STALENESS_WINDOW_MS = 4 * 60 * 60 * 1000;

/** Accepted timestamp shapes: JS Date, Firestore-Timestamp-like, epoch ms, or empty. */
type BriefingTimestamp = Date | { toDate: () => Date } | number | null | undefined;

/** Result of evaluating a briefing's freshness. */
export interface BriefingFreshness {
  /** Muted relative label such as "updated 2 hours ago", or null when no timestamp. */
  label: string | null;
  /** True when the timestamp is older than the staleness window. */
  isStale: boolean;
}

/**
 * Narrows an unknown value to a Firestore-Timestamp-like `{ toDate() }` object.
 */
function isTimestampLike(value: unknown): value is { toDate: () => Date } {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { toDate?: unknown }).toDate === 'function'
  );
}

/**
 * Normalizes any accepted timestamp shape to a valid Date, or null when the
 * input is empty or cannot be resolved to a real point in time.
 */
function normalizeToDate(timestamp: BriefingTimestamp): Date | null {
  if (timestamp === null || timestamp === undefined) {
    return null;
  }

  const date =
    timestamp instanceof Date
      ? timestamp
      : isTimestampLike(timestamp)
        ? timestamp.toDate()
        : new Date(timestamp);

  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Evaluates a briefing's freshness against a reference clock.
 *
 * @param timestamp - When the briefing was last updated. Accepts a JS Date, a
 *   Firestore-Timestamp-like `{ toDate() }`, an epoch-millisecond number, or
 *   null/undefined (handled gracefully — never throws).
 * @param now - Injectable reference time for determinism. Defaults to the
 *   current wall-clock time only when omitted.
 * @returns `{ label, isStale }`, where `label` is a muted relative string like
 *   "updated 2 hours ago". For an empty timestamp, returns a graceful
 *   `{ label: null, isStale: false }`.
 */
export function getBriefingFreshness(
  timestamp: BriefingTimestamp,
  now: Date = new Date(),
): BriefingFreshness {
  // Section 1: graceful handling of missing/invalid input.
  const updatedAt = normalizeToDate(timestamp);
  if (updatedAt === null) {
    return { label: null, isStale: false };
  }

  // Section 2: derive the relative label and staleness from the injected clock.
  const ageMs = now.getTime() - updatedAt.getTime();
  const label = `updated ${formatDistanceStrict(updatedAt, now, { addSuffix: true })}`;
  const isStale = ageMs > STALENESS_WINDOW_MS;

  // Section 3: single happy-path return.
  return { label, isStale };
}
